<script setup lang="ts">
/**
 * PAGE CAGNOTTE — contenu du PANNEAU DE DON sur téléphone (Karim 2026-10-13) :
 * montant en grand, « Ce que permet votre don » (un palier compact à la
 * fois, qu'on fait glisser comme sur ordinateur ; un appui choisit le
 * montant), montants rapides, réduction d'impôt,
 * « Faire un don » et
 * un CLAVIER numérique maison (pas le clavier du téléphone, qui cacherait la
 * moitié du panneau).
 *
 * Mêmes règles que la carte de don : montants rapides 20 / 50 / 100 / 200 €
 * (montantsRapides.ts), minimum 1 €, ouverture de la page
 * de paiement dans un nouvel onglet avec le montant prérempli. Dons UNIQUES
 * seulement : les cagnottes n'acceptent pas de don mensuel.
 *
 * La poignée et l'en-tête signalent le glisser vers le bas (`glisse` /
 * `lache`) : c'est le panneau parent qui se déplace et se ferme.
 */
import { formatNumber } from "@donoor/utils";
import DonateFiscalLigne from "~/components/donate/DonateFiscalLigne.vue";
import type { ImpactOption } from "~/components/donate/DonateImpactOptions.vue";
import { getFileUrl } from "~/services/assets";
import { useCarrouselPaliers } from "~/components/donate/useCarrouselPaliers";
import type { CagnotteAffichee } from "./types";
import { MONTANTS_RAPIDES_CAGNOTTE, montantRapideParDefaut } from "./montantsRapides";

const props = defineProps<{
  cagnotte: CagnotteAffichee;
  fiscal: boolean;
  montants: number[];
  indexDefaut: number;
  /** Paliers « Ce que permet votre don » de la cagnotte. */
  impacts: ImpactOption[];
  couleur?: string | null;
}>();
const emit = defineEmits<{ fermer: []; glisse: [dy: number]; lache: [dy: number] }>();

const { t, locale } = useI18n();
const { formatCurrency } = useFormatLangue();

const devise = computed(() => props.cagnotte.currency || "EUR");
const symbole = computed(() => (devise.value === "EUR" ? "€" : devise.value));
/** Séparateur décimal de la langue : « , » partout sauf en anglais. */
const separateur = computed(() => (String(locale.value).startsWith("en") ? "." : ","));
const montantPrecis = (n: number) =>
  formatCurrency(n, { currency: devise.value, decimals: Number.isInteger(n) ? 0 : 2 });

// ─── Montant saisi (chaîne « 50 », « 12.5 ») ────────────────────────────
const montantsProposes = MONTANTS_RAPIDES_CAGNOTTE;
const saisie = ref(String(montantRapideParDefaut(props.montants, props.indexDefaut)));
const valeur = computed(() => Number.parseFloat(saisie.value) || 0);
const touche = ref(false);

/** Affichage : « 1 250,5 € » — les chiffres tapés, tels quels (virgule comprise). */
const affiche = computed(() => {
  if (!saisie.value) return null;
  const [entier = "0", decimales] = saisie.value.split(".");
  const groupe = formatNumber(Number(entier) || 0, { locale: String(locale.value) });
  return `${groupe}${decimales !== undefined ? separateur.value + decimales : ""} ${symbole.value}`;
});
const tailleMontant = computed(() => ((affiche.value?.length ?? 0) > 9 ? "text-[44px]" : "text-[58px]"));

const erreur = computed(() => {
  if (!saisie.value) return t("don.validationForm.montantRequis");
  if (valeur.value <= 0) return t("don.validationForm.montantPositif");
  if (valeur.value < 1) return t("don.validationForm.montantMinimum");
  return null;
});

function vibrer() {
  try {
    navigator.vibrate?.(6);
  } catch {
    /* pas de vibration : sans importance */
  }
}
/** Touche du clavier : chiffres, virgule (une seule, 2 décimales max), effacer. */
function appui(touche: string) {
  vibrer();
  const s = saisie.value;
  if (touche === "effacer") {
    saisie.value = s.slice(0, -1);
    return;
  }
  if (touche === ".") {
    if (!s.includes(".")) saisie.value = (s || "0") + ".";
    return;
  }
  const [entier = "", decimales] = s.split(".");
  if (decimales !== undefined) {
    if (decimales.length < 2) saisie.value = s + touche;
    return;
  }
  if (entier.length >= 6) return; // 999 999 € au plus
  saisie.value = entier === "0" ? touche : s + touche;
}
function choisir(n: number) {
  vibrer();
  saisie.value = String(n);
}

// ─── « Ce que permet votre don » : un palier à la fois, qu'on fait glisser ─
function imagePalier(image?: string): string | null {
  if (!image) return null;
  if (image.startsWith("http") || image.startsWith("/")) return image;
  return getFileUrl(image, { width: 96, height: 96, fit: "cover", format: "webp", quality: 80 });
}
const {
  indexPalier,
  rail,
  anime,
  styleRail,
  finAnimation,
  debutGlisse: debutGlissePalier,
  pendantGlisse: pendantGlissePalier,
  finGlisse: finGlissePalier,
  bloquerClicApresGlisse,
  roue,
} = useCarrouselPaliers(toRef(props, "impacts"));
// Un montant choisi autrement (bouton rapide, clavier) ne fait PAS tourner le
// carrousel : le palier du même montant est simplement marqué comme choisi,
// qu'il soit affiché maintenant ou quand on y glisse (Karim 2026-10-13).

const TOUCHES = ["1", "2", "3", "4", "5", "6", "7", "8", "9", ".", "0", "effacer"];

// Couleur des boutons rapides de montant (comme la carte de don).
const styleAccent = computed(() => ({
  background: props.couleur
    ? props.couleur
    : "var(--color-main)",
}));

const lienPaiement = computed(
  () => `/c/${props.cagnotte.slug}/payment?montant=${encodeURIComponent(String(Math.round(valeur.value * 100) / 100))}`,
);
function verifier(e: MouseEvent) {
  touche.value = true;
  if (erreur.value) e.preventDefault();
}

// ─── Glisser la poignée vers le bas pour fermer ─────────────────────────
let departY: number | null = null;
function debutGlisse(e: PointerEvent) {
  departY = e.clientY;
  (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
}
function pendantGlisse(e: PointerEvent) {
  if (departY !== null) emit("glisse", Math.max(0, e.clientY - departY));
}
function finGlisse(e: PointerEvent) {
  if (departY === null) return;
  emit("lache", Math.max(0, e.clientY - departY));
  departY = null;
}
</script>

<template>
  <div class="px-5 pb-6" style="padding-bottom: calc(1.5rem + env(safe-area-inset-bottom))">
    <!-- Poignée + en-tête : glisser vers le bas pour fermer -->
    <div
      class="pt-2.5 pb-1 touch-none cursor-grab"
      @pointerdown="debutGlisse"
      @pointermove="pendantGlisse"
      @pointerup="finGlisse"
      @pointercancel="finGlisse"
    >
      <div class="mx-auto h-1.5 w-10 rounded-full bg-gray-200" />
      <div class="mt-3 flex items-center justify-between">
        <p class="text-[15px] text-(--color-ink)" style="font-weight: 700 !important">{{ $t("campagne.pageCagnotte.votreDon") }}</p>
        <button
          type="button"
          class="size-9 rounded-full bg-[#eceef2] ring-1 ring-black/[0.06] text-(--color-ink) flex items-center justify-center active:scale-95 transition-transform"
          :aria-label="$t('commun.actions.fermer')"
          @pointerdown.stop
          @click="emit('fermer')"
        >
          <UIcon name="i-lucide-x" class="size-4.5" />
        </button>
      </div>
    </div>

    <!-- Montant en grand. Hauteurs en fonction de l'écran (svh) : tout tient
         sans défiler, même avec les paliers, sur un téléphone courant. -->
    <div
      class="flex flex-col items-center justify-center"
      :class="impacts.length ? 'h-[clamp(88px,13svh,140px)]' : 'h-[clamp(120px,21svh,180px)]'"
    >
      <p
        class="tabular-nums leading-none whitespace-nowrap transition-[font-size] duration-150"
        :class="[tailleMontant, affiche ? 'text-(--color-ink)' : 'text-gray-300']"
        style="font-weight: 800 !important; letter-spacing: -0.03em"
        aria-live="polite"
      >
        {{ affiche ?? `0 ${symbole}` }}
      </p>
      <p v-if="touche && erreur" class="mt-2 text-[13px] text-red-600">{{ erreur }}</p>
    </div>

    <!-- Montants rapides -->
    <div class="grid grid-cols-4 gap-2">
      <button
        v-for="n in montantsProposes"
        :key="n"
        type="button"
        class="h-12 rounded-2xl text-[15px] transition-all active:scale-95"
        :class="valeur === n && !saisie.endsWith('.') ? 'text-white shadow-sm' : 'bg-white ring-1 ring-gray-200 text-(--color-ink)'"
        :style="valeur === n && !saisie.endsWith('.') ? styleAccent : undefined"
        style="font-weight: 700 !important"
        @click="choisir(n)"
      >
        {{ montantPrecis(n) }}
      </button>
    </div>

    <!-- Ce que permet votre don, sous les montants rapides : un palier compact à
         la fois ; glisser (doigt, souris, pavé tactile) pour passer au suivant,
         en boucle. Un appui choisit le montant. Bordure à l'INTÉRIEUR
         (ring-inset) : le rail masque ce qui dépasse. -->
    <div v-if="impacts.length" class="mt-3">
      <div
        class="overflow-hidden select-none"
        style="touch-action: pan-y"
        @pointerdown="debutGlissePalier"
        @pointermove="pendantGlissePalier"
        @pointerup="finGlissePalier"
        @pointercancel="finGlissePalier"
        @click.capture="bloquerClicApresGlisse"
        @wheel="roue"
      >
        <div
          class="flex items-start"
          :class="anime ? 'transition-transform duration-300 ease-out' : ''"
          :style="impacts.length > 1 ? styleRail : undefined"
          @transitionend.self="finAnimation"
        >
          <div v-for="diapo in impacts.length > 1 ? rail : [{ cle: '0', palier: impacts[0]! }]" :key="diapo.cle" class="w-full shrink-0">
            <button
              type="button"
              class="flex h-14 w-full items-center gap-3 rounded-2xl bg-white pl-2 pr-3.5 text-left transition-shadow"
              :class="valeur === diapo.palier.amount ? 'ring-2 ring-inset' : 'ring-1 ring-inset ring-gray-200'"
              :style="valeur === diapo.palier.amount ? { '--tw-ring-color': 'var(--color-ink)' } : undefined"
              @click="choisir(diapo.palier.amount)"
            >
              <img
                v-if="imagePalier(diapo.palier.image)"
                :src="imagePalier(diapo.palier.image) ?? undefined"
                alt=""
                draggable="false"
                class="size-10 shrink-0 rounded-xl object-cover"
              />
              <span v-else class="flex size-10 shrink-0 items-center justify-center rounded-xl bg-[#eef3ff] text-[#0F3D8C]">
                <UIcon name="i-lucide-heart-handshake" class="size-5" />
              </span>
              <span class="min-w-0 flex-1">
                <span class="block text-[14px] leading-tight text-(--color-ink)" style="font-weight: 800 !important">{{ montantPrecis(diapo.palier.amount) }}</span>
                <span class="block truncate text-[13px] leading-tight text-gray-500">{{ diapo.palier.label }}</span>
              </span>
              <UIcon
                v-if="valeur === diapo.palier.amount"
                name="i-lucide-circle-check"
                class="size-5 shrink-0"
                :style="{ color: 'var(--color-ink)' }"
              />
            </button>
          </div>
        </div>
      </div>
      <!-- Points : où l'on en est, et qu'il y en a d'autres -->
      <div v-if="impacts.length > 1" class="mt-2 flex justify-center gap-1.5" aria-hidden="true">
        <span
          v-for="(_, i) in impacts"
          :key="i"
          class="h-1.5 rounded-full transition-all duration-300"
          :class="i === indexPalier ? 'w-4 bg-(--color-ink)' : 'w-1.5 bg-gray-300'"
        />
      </div>
    </div>

    <!-- Réduction d'impôt, même ligne animée que la carte de don -->
    <DonateFiscalLigne v-if="fiscal" class="mt-5" :final-amount="erreur ? 0 : valeur" :format-amount="montantPrecis" />

    <a
      :href="lienPaiement"
      target="_blank"
      rel="noopener"
      class="mt-5 flex w-full items-center justify-center gap-2 py-3 rounded-full text-white text-[17px] no-underline active:scale-[0.98] transition-[scale,opacity]"
      :class="erreur ? 'opacity-60' : ''"
      :style="styleAccent"
      style="font-weight: 700 !important; color: white !important"
      @click="verifier"
    >
      <UIcon name="i-lucide-heart" class="size-5" />
      {{ $t("campagne.pageCagnotte.faireUnDon") }}
    </a>

    <!-- Clavier numérique -->
    <div class="mt-4 grid grid-cols-3 gap-1.5 select-none">
      <button
        v-for="k in TOUCHES"
        :key="k"
        type="button"
        class="h-[clamp(46px,6.6svh,64px)] rounded-2xl flex items-center justify-center text-[28px] text-(--color-ink) active:bg-gray-100 transition-colors touch-manipulation"
        style="font-weight: 700 !important"
        :aria-label="k === 'effacer' ? $t('campagne.pageCagnotte.effacer') : k === '.' ? $t('campagne.pageCagnotte.virgule') : k"
        @click="appui(k)"
      >
        <UIcon v-if="k === 'effacer'" name="i-lucide-delete" class="size-7" />
        <template v-else>{{ k === "." ? separateur : k }}</template>
      </button>
    </div>
  </div>
</template>
